import { motion, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1] as const;

interface LoadingScreenProps {
  onComplete?: () => void;
}

const SAFFRON_PATH =
  "m36.8212573 59.8023061 51.9271955-21.7455096-20.3617996 53.3109055-53.105335 20.156229z";
const VIOLET_PATH =
  "m36.7608774 48.9058842 51.9271952-21.7455097-20.3617993 53.3109057-53.105335 20.1562288z";
const INDIGO_PATH =
  "m36.7608774 37.2360395 51.9271952-21.7455097-20.3617993 53.3109057-53.105335 20.1562287z";
const SEED_PATH =
  "m52.5568885 55c1.3607898 0 3.4431115-.5419183 3.4431115-2.4758366 0-1.9339182-2.0823217-2.5241634-3.4431115-2.5241634s-3.5568885.5902452-3.5568885 2.5241634c0 1.9339183 2.1960987 2.4758366 3.5568885 2.4758366z";

const SAFFRON_TRANSFORM =
  "matrix(.70710678 .70710678 -.70710678 .70710678 68.119581 -14.874488)";
const VIOLET_TRANSFORM =
  "matrix(.70710678 .70710678 -.70710678 .70710678 60.396962 -18.02328)";
const INDIGO_TRANSFORM =
  "matrix(.70710678 .70710678 -.70710678 .70710678 52.145135 -21.441299)";

/**
 * Branded loading screen — the Sadafal logo assembles itself top-down:
 * indigo (the topmost diamond by position) animates in first, then
 * violet, then saffron, then the white sprout (seed → angled stem →
 * vertical stem → root). The wordmark and "Est. 2010" kicker fade in
 * beneath, with a quiet pulsing dot trio underneath while the video
 * continues to buffer.
 *
 * Animation order is top → middle → bottom; the z-stacking is the
 * reverse — saffron (bottom diamond) is drawn last so it sits on top
 * in the layered overlap. Animation is opacity-only on the diamonds so
 * it can't fight the matrix transform attributes that position each
 * layer.
 */
export function LoadingScreen({ onComplete }: LoadingScreenProps) {
  const reduceMotion = useReducedMotion();

  if (reduceMotion) {
    return (
      <motion.div
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-warm-cream"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5, ease: EASE }}
      >
        <img
          src="/logo.svg"
          alt=""
          className="h-24 w-auto md:h-32"
          aria-hidden="true"
        />
        <p className="mt-8 font-display text-xl text-ink md:text-2xl">
          Sadafal Education
        </p>
      </motion.div>
    );
  }

  // Diamonds fade in opacity-only — no scale, so the matrix transform
  // attribute on each path stays the sole source of positioning.
  const diamondVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 0.846005,
      transition: { duration: 0.65, ease: EASE },
    },
  };

  return (
    <motion.div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-warm-cream"
      initial={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7, ease: EASE }}
    >
      <motion.svg
        viewBox="0 0 104 127"
        className="h-24 w-auto md:h-32"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: {},
          visible: {
            transition: { staggerChildren: 0.28, delayChildren: 0.2 },
          },
        }}
        onAnimationComplete={() => onComplete?.()}
        aria-hidden="true"
      >
        <g fill="none">
          {/* Indigo — top diamond, animates in first; sits at the back */}
          <motion.path
            d={INDIGO_PATH}
            fill="#534bcb"
            transform={INDIGO_TRANSFORM}
            variants={diamondVariants}
          />
          {/* Violet — middle diamond, animates next */}
          <motion.path
            d={VIOLET_PATH}
            fill="#a34474"
            transform={VIOLET_TRANSFORM}
            variants={diamondVariants}
          />
          {/* Saffron — bottom diamond, animates last; drawn last so it
              sits on top of the stack in z */}
          <motion.path
            d={SAFFRON_PATH}
            fill="#efa01d"
            transform={SAFFRON_TRANSFORM}
            variants={diamondVariants}
          />

          {/* Sprout group — fill="#fff", stroke applied per-path
              exactly like the original SVG. The seed (last path) has
              no stroke. */}
          <g fill="#fff">
            {/* Seed at the top of the saffron diamond */}
            <motion.path
              d={SEED_PATH}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 1.05, ease: EASE }}
            />
            {/* Angled stem — from seed (53,54) down-left to (27,64) */}
            <motion.path
              d="m53 54-26 10"
              stroke="#aaa"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{
                pathLength: { duration: 0.55, delay: 1.35, ease: EASE },
                opacity: { duration: 0.2, delay: 1.35 },
              }}
            />
            {/* Vertical stem — from y=63 down to y=96 */}
            <motion.path
              d="m25.5 63v33"
              stroke="#aaa"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: 1, opacity: 1 }}
              transition={{
                pathLength: { duration: 0.6, delay: 1.65, ease: EASE },
                opacity: { duration: 0.2, delay: 1.65 },
              }}
            />
            {/* Root dot at the base */}
            <motion.circle
              cx="25.5"
              cy="98.5"
              r="1"
              stroke="#aaa"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35, delay: 2.1, ease: EASE }}
            />
          </g>
        </g>
      </motion.svg>

      <motion.p
        className="mt-8 font-display text-xl text-ink md:text-2xl"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1.7, ease: EASE }}
      >
        Sadafal Education
      </motion.p>

      <motion.span
        className="mt-2 font-body text-[10px] font-medium tracking-[0.3em] text-saffron-dark uppercase"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.7, delay: 2.0, ease: EASE }}
      >
        Est. 2010
      </motion.span>

      <motion.div
        className="mt-10 flex gap-1.5"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 2.3 }}
        aria-hidden="true"
      >
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className="block h-1.5 w-1.5 rounded-full bg-saffron-dark"
            animate={{ opacity: [0.25, 0.85, 0.25] }}
            transition={{
              duration: 1.4,
              delay: 2.3 + i * 0.18,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </motion.div>
    </motion.div>
  );
}
